import { CartesianScaleOptions, ChartData, ChartOptions } from 'chart.js'
import { FC } from 'react'
import { Bar } from 'react-chartjs-2'

import { Box } from '@mui/material'

import utilsService from '../../services/UtilsService'

type ScaleConfig = {
  title?: string
  precision?: number
  maxY?: number
}

enum YAxisId {
  Y1 = 'y1',
  Y2 = 'y2',
}

enum XAxisId {
  X = 'x',
}

type BarChartProps = {
  data?: ChartData<'bar'>
  scaleConfig?: {
    [YAxisId.Y1]?: ScaleConfig
    [YAxisId.Y2]?: ScaleConfig
    [XAxisId.X]?: CartesianScaleOptions
  }
  /** Formats the axis ticks and tooltip values. Defaults to percentages. */
  valueFormatter?: (value: number) => string
}

const formatPercentageTick = (value: number) => utilsService.formatNumber(value, { mantissa: 0 }) + '%'

export const BarChart: FC<BarChartProps> = ({ data = { datasets: [] }, scaleConfig, valueFormatter }) => {
  const options: ChartOptions<'bar'> = {
    responsive: true,
    // Chart sizes itself to fill the wrapper below. Wrapper uses CSS
    // `aspect-ratio` to maintain the original 2:1 chart ratio. Combining
    // `maintainAspectRatio: true` with a flex/grid parent triggers a
    // Chart.js v4 bug where the chart shrinks on window narrowing but does
    // not grow back when the window widens, and at fractional browser zoom
    // levels (e.g. 90% in Chromium on Windows) it can enter an infinite
    // shrink/expand loop (see Chart.js issue #12177). (GR-2925, GR-2926)
    maintainAspectRatio: false,
    scales: {
      x: {
        ...scaleConfig?.[XAxisId.X],
      },
      y1: {
        position: 'left' as const,
        title: {
          text: scaleConfig?.[YAxisId.Y1]?.title,
          display: !!scaleConfig?.[YAxisId.Y1]?.title,
          padding: { bottom: 16 },
        },
        beginAtZero: true,
        display: !!data?.datasets.find((dataset) => dataset.yAxisID === YAxisId.Y1),
        ticks: {
          precision: scaleConfig?.[YAxisId.Y1]?.precision || 0,
          callback: (value) => (valueFormatter ?? formatPercentageTick)(value as number),
        },
        suggestedMax: scaleConfig?.[YAxisId.Y1]?.maxY,
      },
      y2: {
        position: 'right' as const,
        title: {
          text: scaleConfig?.[YAxisId.Y2]?.title,
          display: !!scaleConfig?.[YAxisId.Y2]?.title,
          padding: { bottom: 16 },
        },
        beginAtZero: true,
        display: !!data?.datasets.find((dataset) => dataset.yAxisID === YAxisId.Y2),
        ticks: {
          precision: scaleConfig?.[YAxisId.Y2]?.precision || 0,
          callback: (value) => (valueFormatter ?? formatPercentageTick)(value as number),
        },
        suggestedMax: scaleConfig?.[YAxisId.Y2]?.maxY,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
      line: {
        borderWidth: 2,
      },
    },
    interaction: {
      intersect: false,
      axis: 'x' as const,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          pointStyle: 'circle' as const,
          usePointStyle: true,
          padding: 16,
        },
      },
      tooltip: {
        position: 'average',
        yAlign: 'bottom',
        titleFont: {
          size: 14,
          weight: 'bolder' as const,
        },
        titleMarginBottom: 14,
        bodySpacing: 6,
        boxPadding: 6,
        usePointStyle: true,
        padding: 10,
        itemSort: (a, b) => {
          return a.element.y - b.element.y
        },
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label
          },
          label: (tooltipItem) => {
            const value = valueFormatter ? valueFormatter(tooltipItem.raw as number) : `${(tooltipItem.raw as number).toFixed(1)}%`
            return ` ${tooltipItem.dataset.label}: ${value}`
          },
        },
      },
    },
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '2 / 1' }}>
      <Bar data={data} options={options} />
    </Box>
  )
}
